/**
 * AppLayout - Responsive layout wrapper
 *
 * Features:
 * - Desktop (≥768px): Sidebar + main content
 * - Mobile (<768px): Main content + bottom tabs (hub pages only)
 * - ScrollToTop on route changes
 * - Command palette integration
 *
 * BOTTOM TABS RULE:
 * Show tabs ONLY on hub pages (dashboard, portfolio, studio).
 * Hide on all focused-work pages (calculate, result, adjust, etc.)
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from '@/hooks/useNavigation';
import { Sidebar } from '@/components/navigation/Sidebar';
import { BottomTabs } from '@/components/navigation/BottomTabs';
import { CommandPalette } from '@/components/navigation/CommandPalette';
import { OfflineBanner } from '@/components/OfflineBanner';
import { SettingsPanel } from '@/components/SettingsPanel';
import { useDashboardMode } from '@/hooks/useDashboardMode';
import { ScrollToTop } from './ScrollToTop';

// Hub pages where bottom tabs should be visible
const TAB_ROUTES = ['/app/dashboard', '/app/portfolio', '/app/insights'];

// Global event for opening settings from anywhere
export const openSettings = () => window.dispatchEvent(new CustomEvent('open-settings'));

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const { pathname } = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { mode: dashboardMode, setMode: setDashboardMode } = useDashboardMode();

  // Listen for open-settings events from anywhere
  useEffect(() => {
    const handler = () => setSettingsOpen(true);
    window.addEventListener('open-settings', handler);
    return () => window.removeEventListener('open-settings', handler);
  }, []);

  const handleSettingsClose = useCallback(() => {
    setSettingsOpen(false);
  }, []);

  // Show tabs only on hub pages
  const showTabs = !isDesktop && TAB_ROUTES.some(route => pathname === route);

  return (
    <div className="min-h-screen min-h-[100dvh] bg-cream-100 flex">
      {/* Scroll reset on route change */}
      <ScrollToTop />

      {/* Offline Banner (global) */}
      <OfflineBanner />

      {/* Command Palette (global) */}
      <CommandPalette />

      {/* Desktop: Sidebar */}
      {isDesktop && <Sidebar />}

      {/* Main Content Area */}
      <main
        className={`
          flex-1 flex flex-col
          ${isDesktop ? 'ml-64' : ''}
          ${showTabs ? 'pb-[calc(3.5rem+env(safe-area-inset-bottom))]' : ''}
        `}
        style={{ maxWidth: '100vw', overflowX: 'clip' }}
      >
        <div className="flex-1 overflow-y-auto" style={{ overflowX: 'clip' }}>
          <div className={isDesktop ? 'max-w-3xl mx-auto w-full' : 'w-full'}>
            {children}
          </div>
        </div>
      </main>

      {/* Mobile: Bottom Tabs (hub pages only) */}
      {showTabs && <BottomTabs />}

      {/* Settings slide-over panel */}
      <SettingsPanel isOpen={settingsOpen} onClose={handleSettingsClose} dashboardMode={dashboardMode} onDashboardModeChange={setDashboardMode} />
    </div>
  );
}

export default AppLayout;
